export function providerCapabilities(connector) {
 const binance=connector==='Binance';
 return {ohlcv:true,depth:binance,aggregateTrades:binance,footprint:binance,historyPagination:binance};
}
export function footprint(trades,tickSize) {
 if(!Number.isFinite(tickSize)||tickSize<=0)throw new Error('Choose a positive price step.');
 const levels=new Map();
 for(const trade of trades){
  if(!Number.isFinite(trade.price)||!Number.isFinite(trade.quantity)||trade.quantity<=0)continue;
  const index=Math.floor((trade.price+tickSize*1e-10)/tickSize),price=Number((index*tickSize).toPrecision(12));
  const level=levels.get(index)||{price,buy:0,sell:0,total:0,delta:0};
  if(trade.side==='buy')level.buy+=trade.quantity;
  else if(trade.side==='sell')level.sell+=trade.quantity;
  else continue;
  level.total=level.buy+level.sell;level.delta=level.buy-level.sell;levels.set(index,level);
 }
 return [...levels.values()].sort((a,b)=>b.price-a.price);
}
export function tickBars(trades,count=100) {
 if(!Number.isInteger(count)||count<1)throw new Error('Choose a positive trade count.');
 const sorted=[...new Map(trades.map(t=>[t.id,t])).values()].sort((a,b)=>a.time-b.time||String(a.id).localeCompare(String(b.id),undefined,{numeric:true}));
 const result=[];
 for(let i=0;i<sorted.length;i+=count){
  const group=sorted.slice(i,i+count);
  if(group.some(t=>!Number.isFinite(t.price)||!Number.isFinite(t.quantity)))continue;
  const prices=group.map(t=>t.price);
  result.push([group[0].time,prices[0],Math.max(...prices),Math.min(...prices),prices.at(-1),group.reduce((s,t)=>s+t.quantity,0)]);
 }
 return result;
}
// Strategies see only completed bars; trades execute at the following bar open.
// Both stop and target touched within one candle resolves to the stop first.
export function backtest(candles,{fast=10,slow=30,capital=100000,allocation=1,feeBps=10,slippageBps=2,stopPercent=0,targetPercent=0}={}) {
 for(const value of [fast,slow,capital,allocation,feeBps,slippageBps,stopPercent,targetPercent])if(!Number.isFinite(value))throw new Error('Use finite backtest settings.');
 if(!Number.isInteger(fast)||!Number.isInteger(slow)||fast<2||slow<=fast||capital<=0||allocation<=0||allocation>1||feeBps<0||feeBps>1000||slippageBps<0||slippageBps>1000||stopPercent<0||stopPercent>=100||targetPercent<0)throw new Error('Invalid backtest settings.');
 if(candles.some(row=>!Array.isArray(row)||row.length<6||!row.slice(0,6).every(Number.isFinite)))throw new Error('Backtest requires finite OHLCV bars.');
 const bars=candles;
 for(let i=0;i<bars.length;i++)if(bars[i][1]<=0||bars[i][4]<=0||bars[i][2]<Math.max(bars[i][1],bars[i][4])||bars[i][3]>Math.min(bars[i][1],bars[i][4])||(i&&bars[i][0]<=bars[i-1][0]))throw new Error('Backtest requires valid, chronological candles.');
 if(bars.length<slow+2)throw new Error('Load more history than the slow moving average.');
 const average=(end,n)=>{let sum=0;for(let j=end-n+1;j<=end;j++)sum+=bars[j][4];return sum/n;};
 let cash=capital,position=null,pending=null,peak=capital,maxDrawdown=0;
 const trades=[],equity=[];
 const fee=feeBps/10000,slip=slippageBps/10000;
 function close(price,time,reason){
  const exit=price*(1-slip),proceeds=position.quantity*exit*(1-fee),pnl=proceeds-position.cost;
  cash+=proceeds;trades.push({...position,exit,time,reason,pnl});position=null;
 }
 for(let i=slow;i<bars.length;i++){
  const [time,open,high,low,price]=bars[i];
  if(pending==='buy'&&!position){
   const budget=cash*allocation,entry=open*(1+slip),quantity=budget/(entry*(1+fee));
   cash-=budget;position={entry,quantity,cost:budget,entryTime:time};
  }else if(pending==='sell'&&position)close(open,time,'signal');
  pending=null;
  if(position){
   const stop=stopPercent?position.entry*(1-stopPercent/100):0,target=targetPercent?position.entry*(1+targetPercent/100):Infinity;
   if(stop&&low<=stop)close(Math.min(open,stop),time,'stop');
   else if(target<Infinity&&high>=target)close(Math.max(open,target),time,'target');
  }
  const value=cash+(position?position.quantity*price:0);
  peak=Math.max(peak,value);maxDrawdown=Math.max(maxDrawdown,(peak-value)/peak*100);
  equity.push({time,value});
  const fastNow=average(i,fast),slowNow=average(i,slow),fastPrev=average(i-1,fast),slowPrev=average(i-1,slow);
  if(fastPrev<=slowPrev&&fastNow>slowNow&&!position)pending='buy';
  if(fastPrev>=slowPrev&&fastNow<slowNow&&position)pending='sell';
 }
 const finalEquity=equity.at(-1).value,wins=trades.filter(t=>t.pnl>0),losses=trades.filter(t=>t.pnl<0),grossProfit=wins.reduce((s,t)=>s+t.pnl,0),grossLoss=-losses.reduce((s,t)=>s+t.pnl,0);
 return {initialCapital:capital,finalEquity,returnPercent:(finalEquity/capital-1)*100,maxDrawdown,closedTrades:trades.length,
  winRate:trades.length?wins.length/trades.length*100:0,profitFactor:grossLoss?grossProfit/grossLoss:null,
  unrealized:position?position.quantity*bars.at(-1)[4]-position.cost:0,position,trades,equity,
  assumptions:'Long-only SMA crossover; completed-bar signals, next-open fills, configured fees/slippage, stop-first if both exits touch. Open positions are marked to last close.'};
}
